import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { BackgroundTexture } from "../components/BackgroundTexture";
import { HeroBanner } from "../components/HeroBanner";
import { EventCard } from "../components/EventCard";
import { BookTableSection } from "../components/BookTableSection";
import { NewsletterForm } from "../components/NewsletterForm";
import { Footer } from "../components/Footer";

export default function Home() {
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([api.getEvents(), api.getGallery(), api.getSettings()])
      .then(([e, g, s]) => {
        setEvents(e.data);
        setGallery(g.data);
        setSettings(s.data);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const featuredEvents = events.filter(e => e.is_featured);
  const displayEvents = featuredEvents.length > 0 ? featuredEvents : events;

  if (!loaded) {
    return (
      <div data-testid="home-page" className="content-layer min-h-screen">
        <BackgroundTexture />
      </div>
    );
  }

  return (
    <div data-testid="home-page" className="content-layer min-h-screen">
      <BackgroundTexture />

      <HeroBanner settings={settings} />

      {displayEvents.length > 0 && (
        <section data-testid="featured-events-section" className="py-10 lg:py-14 relative z-10">
          <div className="max-w-[1400px] mx-auto">
            <div className="px-6 lg:px-12 mb-8 text-center">
              <p className="text-[11px] font-medium tracking-[0.15em] text-white/60 uppercase mb-2">What's On</p>
              <h2 className="font-syne text-2xl lg:text-3xl font-semibold text-white">Featured Events</h2>
            </div>

            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex gap-6 px-6 lg:px-12 pb-4 lg:justify-center">
                {displayEvents.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </div>

            <div className="text-center mt-6">
              <Link to="/events" className="inline-flex items-center gap-1.5 text-sm font-medium text-white/80 hover:text-white transition-colors">
                View All Events <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      <BookTableSection events={events} settings={settings} />

      {gallery.length > 0 && (
        <section className="py-10 lg:py-14 relative z-10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="text-center mb-8">
              <p className="text-[11px] font-medium tracking-[0.15em] text-white/60 uppercase mb-2">Moments</p>
              <h2 className="font-syne text-2xl lg:text-3xl font-semibold text-white">Gallery</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4">
              {gallery.slice(0, 4).map((item) => (
                <div key={item.id} className="aspect-square overflow-hidden rounded-md">
                  <img src={item.image_url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              ))}
            </div>

            <div className="text-center mt-6">
              <Link to="/gallery" className="inline-flex items-center gap-1.5 text-sm font-medium text-white/80 hover:text-white transition-colors">
                View All <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      <section data-testid="newsletter-section" className="py-10 lg:py-14 relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="panel-light rounded-md p-8 lg:p-12 max-w-2xl mx-auto text-center">
            <p className="text-[11px] font-medium tracking-[0.15em] text-[#2D1810]/40 uppercase mb-2">Stay Updated</p>
            <h2 className="font-syne text-2xl lg:text-3xl font-semibold text-[#2D1810] mb-3">Join the Guest List</h2>
            <p className="text-[#2D1810]/60 text-sm mb-6">Sign up for exclusive offers and event announcements.</p>
            <NewsletterForm />
          </div>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
}
